import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DeleteOutlined } from "@ant-design/icons";
import type { SchemaElement, SchemaRegion } from "../../../api/restaurantSchema";
import {
  PanelDivider,
  PanelField,
  PanelHeading,
  RegionSelect,
  SeatsPicker,
} from "./tablesPanelControls";
import { DANGER_BUTTON, PRIMARY_BUTTON, SECONDARY_BUTTON } from "./tablesPanelStyles";
import type { TablesConfigActions } from "./useTablesConfigActions";

const DEFAULT_SEATS = 4;

/**
 * Панелът при НЯКОЛКО избрани маси: общи места („Приложи"), местене в друга
 * зала (при повече от един регион), изтриване на избраните. Заявките вървят
 * една по една — докато текат, бутоните са спрени. `isWorking` и пускането
 * (`onRun`) са в страницата: тя замразява избора, докато цикълът тече.
 */
const BulkEditPanel = ({
  tables,
  region,
  regions,
  actions,
  isWorking,
  onRun,
  onDelete,
  onClearSelection,
}: {
  tables: SchemaElement[];
  region: SchemaRegion;
  regions: SchemaRegion[];
  actions: TablesConfigActions;
  isWorking: boolean;
  onRun: (work: () => Promise<unknown>) => void;
  onDelete: () => void;
  onClearSelection: () => void;
}) => {
  const { t } = useTranslation();
  const commonSeats = tables.every((table) => table.seats === tables[0]?.seats) ? tables[0]?.seats : null;
  const [seats, setSeats] = useState(commonSeats ?? DEFAULT_SEATS);
  const [targetRegion, setTargetRegion] = useState<SchemaRegion | null>(null);
  const otherRegions = regions.filter((candidate) => candidate.id !== region.id);

  return (
    <div className="flex flex-col gap-5">
      <PanelHeading
        kicker={region.name}
        title={t("tablesConfig.bulk.title", { count: tables.length })}
        onClose={onClearSelection}
      />

      <PanelField label={t("tablesConfig.bulk.seats")}>
        <SeatsPicker value={seats} onChange={setSeats} disabled={isWorking} />
        <button
          type="button"
          disabled={isWorking || tables.every((table) => table.seats === seats)}
          onClick={() => onRun(() => actions.setSeatsForTables(tables, seats))}
          className={PRIMARY_BUTTON}
        >
          {t("tablesConfig.bulk.apply")}
        </button>
      </PanelField>

      {otherRegions.length > 0 && (
        <PanelField label={t("tablesConfig.bulk.moveTo")}>
          <RegionSelect
            regions={otherRegions}
            value={targetRegion?.id ?? null}
            onChange={setTargetRegion}
            placeholder={t("tablesConfig.bulk.chooseRegion")}
            ariaLabel={t("tablesConfig.bulk.moveTo")}
            disabled={isWorking}
          />
          <button
            type="button"
            disabled={isWorking || !targetRegion}
            onClick={() => targetRegion && onRun(() => actions.moveTables(tables, targetRegion))}
            className={SECONDARY_BUTTON}
          >
            {t("tablesConfig.bulk.move")}
          </button>
        </PanelField>
      )}

      <PanelDivider />
      <div className="flex flex-col gap-2">
        <button type="button" onClick={onDelete} disabled={isWorking} className={DANGER_BUTTON}>
          <DeleteOutlined className="text-[14px]" />
          {t("tablesConfig.bulk.delete")}
        </button>
        <button type="button" onClick={onClearSelection} disabled={isWorking} className={SECONDARY_BUTTON}>
          {t("tablesConfig.bulk.cancel")}
        </button>
      </div>
    </div>
  );
};

export default BulkEditPanel;
